import {
  FeatureFlagsContext,
  FeatureFlagsContextType,
} from "@/config/featureFlags/FeatureFlagsContext";
import {
  PriceCalculatorBookingQuery,
  PriceCalculatorBookingQueryVariables,
} from "@/gql/graphql";
import { ApolloError, gql, useLazyQuery } from "@apollo/client";
import { useContext, useState } from "react";
import { BookingResultData, MovementType } from "../types/types";
import { useMultiLegPriceCalculation } from "./UseMultiLegPriceCalculation";
import { usePriceCalculation } from "./UsePriceCalculation";
import { fetchBookingFragment } from "../gql/fragments";
import {
  transformMultiLegBookingToPriceCalculatorInput,
  transformSectionalBookingToPriceCalculatorInput,
  transformQueryToBookingSearchResultData,
  transformQueryToMultiLegBookingSearchResultData,
} from "../lib/transformers";

export const useBookingPriceCalculation = (
  setBookingResult: React.Dispatch<
    React.SetStateAction<BookingResultData | undefined>
  >,
  includePendingOffered: boolean = false
) => {
  const [calculatePriceError, setCalculatePriceError] = useState<
    ApolloError | undefined
  >();
  const [calculateMultiLegPriceError, setCalculateMultiLegPriceError] =
    useState<ApolloError | undefined>();

  const { featureFlags } = useContext(
    FeatureFlagsContext
  ) as FeatureFlagsContextType;

  const { calculatePrice, loading: priceCalculationLoading } =
    usePriceCalculation();

  const { calculateMultiLegPrice, loading: multiLegCalculationLoading } =
    useMultiLegPriceCalculation();

  const queryPriceCalculatorBooking = gql`
    ${fetchBookingFragment}
    query PriceCalculatorBooking($bookingNum: ID!, $consignmentVersion: Int!) {
      priceCalculator {
        bookings {
          byId(id: $bookingNum, version: $consignmentVersion) {
            ...FetchBooking
          }
        }
      }
    }
  `;

  const [
    fetchPriceCalculatorBooking,
    { loading: bookingloading, error: fetchBookingError },
  ] = useLazyQuery<
    PriceCalculatorBookingQuery,
    PriceCalculatorBookingQueryVariables
  >(queryPriceCalculatorBooking, {
    onCompleted: async (data) => {
      const bookingList = data.priceCalculator.bookings.byId;
      const bookingData = data?.priceCalculator?.bookings?.byId[0];
      const routeLegs = data?.priceCalculator.bookings.byId.map(
        (routeLeg) => routeLeg.sailingBookingConsignment
      );

      if (bookingData) {
        const sectionalBookingTransformedData =
          transformSectionalBookingToPriceCalculatorInput(
            bookingData,
            includePendingOffered
          );
        const multiLegBookingTransformedData =
          transformMultiLegBookingToPriceCalculatorInput(
            bookingData,
            routeLegs,
            includePendingOffered
          );
        if (
          bookingData.movementType.code === MovementType.SECTIONAL.charAt(0)
        ) {
          const { data: priceCalculationData, error } = await calculatePrice({
            variables: {
              input: sectionalBookingTransformedData,
            },
          });

          if (priceCalculationData) {
            return setBookingResult(
              transformQueryToBookingSearchResultData(
                priceCalculationData,
                bookingList
              )
            );
          } else if (error) {
            setCalculatePriceError(error);
          }
        } else if (
          bookingData.movementType.code === MovementType.MULTILEG.charAt(0)
        ) {
          const { data: priceCalculationMultiLegData, error } =
            await calculateMultiLegPrice({
              variables: {
                input: multiLegBookingTransformedData,
              },
            });
          if (priceCalculationMultiLegData) {
            return setBookingResult(
              transformQueryToMultiLegBookingSearchResultData(
                priceCalculationMultiLegData,
                bookingList
              )
            );
          } else if (error) {
            setCalculateMultiLegPriceError(error);
          }
        }
      }
    },
  });

  return {
    fetchPriceCalculatorBooking,
    loading:
      bookingloading || priceCalculationLoading || multiLegCalculationLoading,
    error:
      calculateMultiLegPriceError || calculatePriceError || fetchBookingError,
  };
};
